import React, { useState, useEffect } from 'react'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Drawer, Button, Input, message, Typography, Radio, Space, Divider } from 'antd'
import { Alert } from '@mercadoni/gungnir'
import { useTranslation } from 'react-i18next'
import { cancelJob, getReasons } from '../../flag-management.services'
import { REASON_PARAMS } from '../../../../utils/constants'

const { v4: uuidv4 } = require('uuid')

const { TextArea } = Input

const JobCancel = Form.create()((props) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [comment, setComment] = useState('')
  const [textAreaLength, setTextAreaLength] = useState(0)
  const [cancellationReasonId, setCancellationReasonId] = useState(null)
  const [cancellationReasons, setCancellationReasons] = useState([])
  const [isReasonOpen, setIsReasonOpen] = useState(false)
  const [resetReasons, setResetReasons] = useState(null)
  const { Title } = Typography

  const cleanFields = () => {
    setComment('')
    setCancellationReasonId(null)
    setIsReasonOpen(false)
    setResetReasons(null)
  }

  const commentJob = async (comment) => {
    const id = uuidv4()
    setLoading(true)
    try {
      const sendCancellation = await cancelJob(
        props.job.id,
        comment,
        cancellationReasonId,
        REASON_PARAMS.CANCELLATION,
        id,
        isReasonOpen
      )
      if (sendCancellation) {
        message.success(t('jobs.jobCancel.cancelled'))
        if (props.jobsSearch) {
          props.setSelectedJob(sendCancellation)
          props.getJobsList()
        }
        props.form.resetFields()
        props.handleJobCancel()
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
    }
  }

  const updateJobComment = (event) => {
    event.preventDefault()
    props.form.validateFields((err) => {
      if (!err) {
        commentJob(comment)
      }
    })
  }

  const onChangeComment = ({ target: { value } }) => {
    setComment(value)
    setTextAreaLength(value.length)
  }

  const getReasonTitle = (id) => {
    let reason = cancellationReasons.find((obj) => obj.id === id)
    return reason.title
  }

  const getReasonStatus = (id) => {
    let reason = cancellationReasons.find((obj) => obj.id === id)
    return reason.metadata.openQuestion
  }

  const onChangeReason = (e) => {
    setComment(getReasonTitle(e.target.value))
    setIsReasonOpen(getReasonStatus(e.target.value))
    setCancellationReasonId(e.target.value)
    setResetReasons(e.target.value)
    setTextAreaLength(0)
  }

  const cancellationReasonsBanner = (
    <div className='cancellation-reasons-banner'>
      <div className='cancellation-reasons-icon-banner'>
        <Alert />
      </div>
      <span className='cancellation-reasons-banner-text'>
        {t('jobs.jobCancel.form.cancellation-reasons-banner-text')}
      </span>
    </div>
  )

  const emptyReasonsMessage = (
    <div className='empty-reasons-message'>
      <img src='/error.png' alt='error' />
      <p className='empty-reasons-message-description'>{t('jobs.jobCancel.form.cancellation-reasons-empty')}</p>
    </div>
  )

  const addReasons = () => {
    return (
      <React.Fragment>
        <div className='cancellation-reasons-drawer'>
          <React.Fragment>
            {cancellationReasonsBanner}
            <Title level={4} className='select-cancellation-reason-title'>
              {t('jobs.jobCancel.form.cancellation-reasons-tittle')}
            </Title>
            {cancellationReasons.length < 1 ? emptyReasonsMessage : null}
            <Radio.Group onChange={onChangeReason} value={resetReasons}>
              <Space direction='vertical'>
                {cancellationReasons.map((reason) => {
                  const isReasonEnabled = reason.metadata.enabled

                  if (isReasonEnabled) {
                    return (
                      <Radio value={reason.id} className='cancellation-reasons-radio'>
                        {reason.title}
                      </Radio>
                    )
                  }
                  return null
                })}
              </Space>
            </Radio.Group>
            {isReasonOpen ? (
              <>
                <TextArea
                  className='cancellation-reasons-comment-text'
                  defaultValue={''}
                  rows={4}
                  placeholder={t('jobs.jobCancel.form.comment')}
                  onChange={onChangeComment}
                  showCount
                  maxLength={200}
                />
              </>
            ) : null}
            {isReasonOpen && textAreaLength < 5 ? (
              <div className='cancellation-reason-comment-minimum'>
                {t('jobs.jobCancel.form.cancellation-reasons-minimum-comment')}{' '}
              </div>
            ) : null}
          </React.Fragment>
        </div>
      </React.Fragment>
    )
  }

  const { job } = props

  useEffect(() => {
    const getReasonsList = async () => {
      setLoading(true)
      const reasons = await getReasons(REASON_PARAMS.CANCELLATION)
      if (reasons) {
        setCancellationReasons(reasons)
      }
      setLoading(false)
    }

    cleanFields()

    if (props.visible) {
      getReasonsList()
    }
  }, [props.visible])

  return (
    <Drawer title={t('jobs.jobCancel.form.title')} width={500} onClose={props.handleJobCancel} visible={props.visible}>
      <Form layout='vertical' onSubmit={updateJobComment}>
        <div>
          <p>{job && job.params ? job.params.comment : ''}</p>
        </div>
        {addReasons()}
        <div
          style={{
            textAlign: 'right'
          }}
        >
          <Divider />
          <Button onClick={props.handleJobCancel} style={{ marginRight: 8 }}>
            {t('jobs.jobCancel.form.cancel-button')}
          </Button>
          <Button
            loading={loading}
            htmlType='submit'
            disabled={(isReasonOpen && textAreaLength < 5) || cancellationReasonId === null}
            type='primary'
          >
            {t('jobs.jobCancel.form.accept-button')}
          </Button>
        </div>
      </Form>
    </Drawer>
  )
})

export default JobCancel
